import classNames from "classnames";
import { DEFAULT_SELECTED_MENU } from "constants/AppConstants";
import { SelectedMenu } from "helpers/usersettings/usersettings";
import React, { MouseEvent, useEffect, useRef, useState } from "react";
import StickyBox from "react-sticky-box";
import MainSideMenu from "./MainSideMenu";
import "./Sidebar.scss";
import SidebarFooter from "./SidebarFooter";
import SidebarMenuSwitch from "./SidebarMenuSwitch";
import TexadaLogo from "./TexadaLogo";

interface SidebarProps {
  toggleSideBar: boolean;
  toggleButtonOnClick: () => void;
  isCollapsed: boolean;
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  isMobile: boolean;
}

export type TSelectedMenu = "texada-web" | "gateway" | "settings";

const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  setIsCollapsed,
  ...props
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const menuSwitchRef = useRef<HTMLDivElement>(null);
  const [isNavOpen, setIsNavOpen] = useState(!isCollapsed || isHovered);
  const [selectedMenu, setSelectedMenu] = useState<TSelectedMenu>(
    SelectedMenu.get() ?? DEFAULT_SELECTED_MENU
  );

  const footerHeight = 60;

  const handleHoverClick = () => {
    setIsCollapsed(false);
    setIsHovered(false);
    setIsNavOpen(true);
  };

  const handleClick = () => {
    setIsCollapsed(true);
    setIsNavOpen(false);
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    e.stopPropagation();
    const nav = document.querySelector("#side_nav_menu");
    if (nav && isCollapsed) {
      nav.classList.add("hovered");
      setIsHovered(true);
      setIsNavOpen(true);
    }
  };

  const handleMouseLeave = () => {
    const nav = document.querySelector("#side_nav_menu.hovered");
    if (nav && isCollapsed) {
      nav.classList.remove("hovered");
      setIsHovered(false);
      setIsNavOpen(false);
    }
  };

  const handleNavOnClick = (e: MouseEvent<HTMLElement>) => {
    if (
      e.currentTarget.className.includes("show_side_bar") &&
      !menuSwitchRef?.current?.contains(e.target as Node)
    ) {
      props.toggleButtonOnClick();
    }
  };

  useEffect(() => {
    if (SelectedMenu.get() !== selectedMenu) SelectedMenu.save(selectedMenu);
  }, [selectedMenu]);

  return (
    <nav
      id="side_nav_menu"
      aria-expanded={isCollapsed}
      className={classNames({
        collapsed: isCollapsed,
        show_side_bar: props.toggleSideBar
      })}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleNavOnClick}
    >
      <StickyBox offsetBottom={footerHeight}>
        <TexadaLogo
          isNavOpen={isNavOpen}
          isMobile={props.isMobile}
          toggleButtonOnClick={props.toggleButtonOnClick}
        />
        <MainSideMenu
          isNavOpen={isNavOpen}
          isMobile={props.isMobile}
          selectedMenu={selectedMenu}
        />
      </StickyBox>
      <SidebarFooter
        handleHoverClick={handleHoverClick}
        handleClick={handleClick}
        isMobile={props.isMobile}
        isCollapsed={isCollapsed}
        isHovered={isHovered}
        SidebarMenuSwitch={
          <SidebarMenuSwitch
            ref={menuSwitchRef}
            selectedMenu={selectedMenu}
            setSelectedMenu={setSelectedMenu}
          />
        }
      />
    </nav>
  );
};

export default Sidebar;
